import { useEffect } from 'react';
import { CoreGlobalFooter } from '@care/navigation';
import { Grid } from '@mui/material';
import dynamic from 'next/dynamic';
import PageMeta from '@/components/PageMeta';
import { SeoGlobalNavbar } from '@/components/common/SeoGlobalNavbar';
import useFeatureFlag from '@/components/hooks/useFeatureFlag';
import { seoArticlesList_seoArticlesList_SEOArticlesListSuccess_seoArticlesList as SEOArticlesList } from '@/__generated__/seoArticlesList';
import { seoActivityCategoryLinkSections_seoActivityCategoryLinkSections_SEOActivityCategoryLinkSectionsSuccess as SEOActivityCategoryLinkSections } from '@/__generated__/seoActivityCategoryLinkSections';
import {
  AmplitudeEvent,
  trackAmplitudeEvent,
  setDeviceDataFromHeaders,
  setPagePropertiesFromData,
} from '@/lib/analyticsHelper';
import { UI_FEATURE_FLAGS } from '@/constants';
import HeroSection from './HeroSection';
import Seasonality from './Seasonality';
import InternalLinks from './InternalLinks';

const ArticleSection = dynamic(() => {
  return import('@/components/common/ArticleSection/ArticleSection');
});
const CategorySection = dynamic(() => {
  return import('@/components/pages/campsAndActivities/GreaterCareRebrand/CategorySection');
});
const ValuePropSection = dynamic(() => {
  return import('@/components/pages/campsAndActivities/GreaterCareRebrand/ValuePropSection');
});

export type LandingPageProps = {
  deviceChar: {
    device_os: string;
    is_mobile: string;
    mobile_browser_version: string;
    mobile_browser: string;
  };
  userGeoInfo: string;
  activityCategoryLinkSections: SEOActivityCategoryLinkSections | null;
  seoArticlesListData: SEOArticlesList | null;
};

const LandingPage = ({
  deviceChar,
  userGeoInfo,
  activityCategoryLinkSections,
  seoArticlesListData,
}: LandingPageProps) => {
  const metaTitle = 'Explore: kids activities, camps and classes - Care.com';
  const metaDescription = 'Book the best kids activities, camps and classes from local providers';
  const canonicalUrl = 'https://www.care.com/explore';

  const isCookingCategoryFlagEnabled =
    useFeatureFlag(UI_FEATURE_FLAGS.OOH_ACTIVITIES_COOKING_CATEGORY.id)?.variationIndex === 0;

  // Filter the linkSections based on the flag
  const filteredActivityCategoryLinkSections: SEOActivityCategoryLinkSections | null =
    activityCategoryLinkSections
      ? {
          __typename: 'SEOActivityCategoryLinkSectionsSuccess',
          linkSections:
            activityCategoryLinkSections.linkSections?.filter(
              (section) => isCookingCategoryFlagEnabled || section.id !== 'COOKING'
            ) ?? [],
        }
      : null;

  useEffect(() => {
    const pageProperties = {
      seo_page_id: 1539,
      screen_name: 'Explore landing page',
      screen_template: 'Explore landing page',
    };
    if (deviceChar && Object.keys(deviceChar).length > 0) {
      setPagePropertiesFromData(pageProperties);
      setDeviceDataFromHeaders(deviceChar);
    }
  }, [deviceChar]);

  useEffect(() => {
    trackAmplitudeEvent(AmplitudeEvent.seoPageViewed, {
      seo_page_type: 'Explore landing page',
      page_template: 'Explore landing page',
      seo_page_title: 'Find kids activities and camps— without the headaches',
    });
  }, []);

  return (
    <Grid container>
      <Grid item xs={12}>
        <PageMeta
          title={metaTitle}
          metaDescriptions={metaDescription}
          canonicalUrl={canonicalUrl}
        />
        <SeoGlobalNavbar />
        <HeroSection
          userGeoInfo={userGeoInfo}
          activityCategoryLinkSections={filteredActivityCategoryLinkSections}
        />
        <CategorySection />
        <ValuePropSection />
        <Seasonality />

        {seoArticlesListData?.articles && (
          <ArticleSection articles={seoArticlesListData.articles} />
        )}
        <InternalLinks activityCategoryLinkSections={filteredActivityCategoryLinkSections} />
        <CoreGlobalFooter variant="inverse" />
      </Grid>
    </Grid>
  );
};

export default LandingPage;
